import React, { useState } from "react";
import { DSBaseTable, DSTableCell } from "../../system-design/Table";
import { Column } from "../../system-design/Table/DSBaseTable/components/DSBaseTable";
import useClocksInfiniteQuery from "../hooks/useClocksInfiniteQuery";
import useClockQuery from "../hooks/useClockQuery";
import { ClocksListResponse } from "../../../Types";
import DSTextField from "../../system-design/DSTextField";
import DSBox from "../../system-design/DSBox";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSErrorButton from "../../system-design/DSErrorButton";
import ConfirmationModal from "../../ConfirmationModal";

interface ImportDevicesFavoritesDialogProps {
  onUpdateFavorites?: (newFavorites: string[]) => void;
  onClose?: () => void;
}

const ImportDevicesFavoritesDialog = ({
  onUpdateFavorites,
  onClose,
}: ImportDevicesFavoritesDialogProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDevice, setSelectedDevice] =
    useState<ClocksListResponse | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const {
    data: devices,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
  } = useClocksInfiniteQuery({
    query: searchQuery,
    registrationStatus: "ALL",
  });

  const { data: deviceData, isLoading: isFetchingFavorites } = useClockQuery(
    selectedDevice?.id,
    { enabled: confirmDialogOpen },
  );
  const favorites = deviceData?.favorites?.value || [];

  const columns: Column<ClocksListResponse>[] = [
    {
      field: "id",
      label: "Device ID",
      size: 50,
      sortable: false,
    },
    {
      field: "idf",
      label: "IDF",
      size: 50,
      sortable: false,
    },
  ];

  const rowBuilder = (device: ClocksListResponse) => {
    const isSelected = selectedDevice?.id === device.id;
    const selectedRowStyle = isSelected
      ? { backgroundColor: "rgba(25, 118, 210, 0.08)" }
      : {};
    const rowClickStyle = { cursor: "pointer" };

    return (
      <>
        <DSTableCell
          key="id"
          onClick={() => setSelectedDevice(device)}
          sx={{
            width: "50%",
            ...selectedRowStyle,
            ...rowClickStyle,
          }}
        >
          {device.id}
        </DSTableCell>
        <DSTableCell
          key="idf"
          onClick={() => setSelectedDevice(device)}
          sx={{
            width: "50%",
            ...selectedRowStyle,
            ...rowClickStyle,
          }}
        >
          {device.idf || "N/A"}
        </DSTableCell>
      </>
    );
  };

  if (error) {
    return (
      <DSBox p={3}>
        <DSBox sx={{ color: "error.main", textAlign: "center" }}>
          Error loading devices: {error.message}
        </DSBox>
      </DSBox>
    );
  }

  return (
    <DSBox
      p={3}
      sx={{
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <DSBox mb={2}>
        <DSTextField
          label="Search devices..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          fullWidth
        />
      </DSBox>

      {isLoading && (!devices || devices.length === 0) ? (
        <DSBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          flex={1}
        >
          <DSCircularProgress />
        </DSBox>
      ) : (
        <DSBox sx={{ maxHeight: "80vh", overflow: "auto" }}>
          <DSBaseTable
            data={devices || []}
            columns={columns}
            rowBuilder={rowBuilder}
            fetchMore={hasNextPage ? fetchNextPage : undefined}
            defaultRowsPerPage={25}
          />
        </DSBox>
      )}

      <DSBox mt={2} display="flex" justifyContent="flex-end" gap={2}>
        <DSErrorButton
          onClick={() => {
            onClose?.();
          }}
        >
          Close
        </DSErrorButton>
        <DSPrimaryButton
          onClick={() => setConfirmDialogOpen(true)}
          disabled={!selectedDevice}
        >
          Import Selected Favorites
        </DSPrimaryButton>
      </DSBox>

      {confirmDialogOpen && (
        <ConfirmationModal
          open={confirmDialogOpen}
          message={
            isFetchingFavorites
              ? "Loading favorites..."
              : `Are you sure you want to import ${selectedDevice?.idf || "this device"}'s favorites? `
          }
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={() => {
            if (isFetchingFavorites) return;
            onUpdateFavorites?.(favorites);
            setConfirmDialogOpen(false);
            onClose?.();
          }}
        />
      )}
    </DSBox>
  );
};

export default ImportDevicesFavoritesDialog;
